import {
  BTLoggerProps,
  HandleResponseErrorProps,
  ResponseErrorData,
} from "@api/internal/utils/types";
import { LambdaLogger } from "@sphtech/web2-logger";
import { WebCoreSecretsManager } from "@sphtech/web2-secrets";

/**
 * Handles and logs response errors in a consistent manner.
 * @param message - Custom error message to log.
 * @param error - The error object containing code and message.
 * @returns A standardized error response object.
 * @template DataType - The type of the data in the successful response.
 */
export const handleResponseError = ({
  message,
  error,
  shouldLogError = true,
}: HandleResponseErrorProps): ResponseErrorData => {
  if (shouldLogError) {
    const logger = LambdaLogger.getInstance();
    logger.error(message, error);
  }

  return {
    success: false,
    error,
  };
};

/**
 * Retrieves the request headers needed for API calls, including the API key from secrets.
 * @returns An object containing the request headers, or undefined if the API key is not found.
 */
export const getRequestHeader = async () => {
  const btApiKey = (await WebCoreSecretsManager.getWeb2Secret("BT_API_KEY"))
    .value;

  if (!btApiKey) return;

  return {
    "x-api-key": btApiKey,
  };
};

/**
 * Helper function to log a message. Can only be used in server side.
 * @param message The message you want to log.
 * @param type The type of message.
 * @return {void}
 */
export const btLogger = ({ message, type }: BTLoggerProps): void => {
  const logger = LambdaLogger.getInstance();

  switch (type) {
    case "info":
      logger.info(message);
      console.info(message); // eslint-disable-line no-console
      break;

    case "error":
      logger.error(message);
      console.error(message); // eslint-disable-line no-console
      break;

    case "warn":
      logger.warn(message);
      console.warn(message); // eslint-disable-line no-console
      break;

    case "debug":
    default:
      logger.debug(message);
      console.debug(message); // eslint-disable-line no-console
      break;
  }
};
